/**
 * Explanation result card.
 * Displays a structured formula explanation using ResultCard:
 *   - Main card: formula + detected functions + explanation text
 *   - Breakdown card: decomposition table
 *   - Suggestions, warnings, errors as sub-sections
 */

import React, { useMemo } from 'react';
import {
  Text,
  Badge,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  LightbulbRegular,
  CopyRegular,
  TableRegular,
} from '@fluentui/react-icons';
import { ResultCard, type ResultCardAction } from '../common/ResultCard';
import { CodeBlock } from '../common/CodeBlock';
import type { FormulaBreakdownPart, FormulaParseMode } from '@excel-ai-studio/shared';

interface ExplanationCardProps {
  formula: string;
  explanation: string;
  breakdown?: readonly FormulaBreakdownPart[];
  detectedFunctions?: readonly string[];
  suggestions?: readonly string[];
  warnings?: readonly string[];
  errors?: readonly string[];
  parseMode?: FormulaParseMode;
  confidenceScore?: number;
  expertDiagnostics?: {
    performance: readonly { text: string; severity: 'low' | 'medium' | 'high' }[];
    references: readonly { text: string; severity: 'low' | 'medium' | 'high' }[];
    stability: readonly { text: string; severity: 'low' | 'medium' | 'high' }[];
  };
}

function getParseModeBadge(mode: FormulaParseMode): { label: string; color: 'success' | 'warning' | 'danger' } {
  if (mode === 'strict-json') return { label: 'Fiabilite elevée', color: 'success' };
  if (mode === 'extracted-json') return { label: 'Fiabilite moyenne', color: 'warning' };
  return { label: 'Verification recommandee', color: 'danger' };
}

function getSeverityBadge(severity: 'low' | 'medium' | 'high'): {
  label: string;
  color: 'success' | 'warning' | 'danger';
} {
  if (severity === 'low') return { label: 'Faible', color: 'success' };
  if (severity === 'medium') return { label: 'Moyen', color: 'warning' };
  return { label: 'Eleve', color: 'danger' };
}

function severityRank(severity: 'low' | 'medium' | 'high'): number {
  if (severity === 'high') return 0;
  if (severity === 'medium') return 1;
  return 2;
}

export const ExplanationCard: React.FC<ExplanationCardProps> = ({
  formula,
  explanation,
  breakdown,
  detectedFunctions,
  suggestions,
  warnings,
  errors,
  parseMode,
  confidenceScore,
  expertDiagnostics,
}) => {
  const explanationActions = useMemo<ResultCardAction[]>(() => [
    {
      key: 'copy-explanation',
      label: 'Copier l\'explication',
      icon: <CopyRegular />,
      feedbackLabel: 'Copie !',
      onClick: () => navigator.clipboard.writeText(explanation),
    },
  ], [explanation]);

  const sortedDiagnostics = useMemo(() => {
    if (!expertDiagnostics) return undefined;
    const sortBySeverity = <T extends { severity: 'low' | 'medium' | 'high' }>(items: readonly T[]): T[] =>
      [...items].sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
    return {
      performance: sortBySeverity(expertDiagnostics.performance),
      references: sortBySeverity(expertDiagnostics.references),
      stability: sortBySeverity(expertDiagnostics.stability),
    };
  }, [expertDiagnostics]);

  return (
    <>
      {/* ── Main explanation card ── */}
      <ResultCard
        title="Explication"
        icon={<LightbulbRegular />}
        variant="explanation"
        actions={explanationActions}
      >
        {/* Formula */}
        <CodeBlock code={formula} label="Formule analysee" />
        {parseMode && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text size={200} style={{ color: 'var(--text-secondary)' }}>
              Mode de parsing: <strong>{parseMode}</strong>
            </Text>
            <Badge size="small" color={getParseModeBadge(parseMode).color}>
              {getParseModeBadge(parseMode).label}
            </Badge>
          </div>
        )}
        {typeof confidenceScore === 'number' && (
          <Text size={200} style={{ color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
            Confiance estimee: <strong>{confidenceScore}%</strong>
          </Text>
        )}

        {/* Detected functions */}
        {detectedFunctions && detectedFunctions.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <Text size={200} weight="semibold" style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
              Fonctions detectees
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {detectedFunctions.map((fn) => (
                <Badge key={fn} appearance="outline" size="small" color="informative">
                  {fn}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Explanation text */}
        <div style={{ marginTop: 10 }}>
          <Text size={200} weight="semibold" style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
            Description
          </Text>
          <Text size={300} style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
            {explanation}
          </Text>
        </div>

        {sortedDiagnostics && (
          <div style={{ marginTop: 10, border: '1px solid var(--border-color)', borderRadius: 6, padding: 8 }}>
            <Text size={200} weight="semibold" style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
              Diagnostic expert
            </Text>
            {sortedDiagnostics.performance.length > 0 && (
              <>
                <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 2 }}>
                  Performance
                </Text>
                <ul style={{ margin: 0, paddingLeft: 18, marginBottom: 6 }}>
                  {sortedDiagnostics.performance.map((item, i) => (
                    <li key={`perf-${i}`} style={{ fontSize: 13, marginBottom: 2 }}>
                      <span>{item.text}</span>{' '}
                      <Badge size="tiny" color={getSeverityBadge(item.severity).color}>
                        {getSeverityBadge(item.severity).label}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {sortedDiagnostics.references.length > 0 && (
              <>
                <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 2 }}>
                  References
                </Text>
                <ul style={{ margin: 0, paddingLeft: 18, marginBottom: 6 }}>
                  {sortedDiagnostics.references.map((item, i) => (
                    <li key={`ref-${i}`} style={{ fontSize: 13, marginBottom: 2 }}>
                      <span>{item.text}</span>{' '}
                      <Badge size="tiny" color={getSeverityBadge(item.severity).color}>
                        {getSeverityBadge(item.severity).label}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {sortedDiagnostics.stability.length > 0 && (
              <>
                <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 2 }}>
                  Stabilite
                </Text>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {sortedDiagnostics.stability.map((item, i) => (
                    <li key={`stability-${i}`} style={{ fontSize: 13, marginBottom: 2 }}>
                      <span>{item.text}</span>{' '}
                      <Badge size="tiny" color={getSeverityBadge(item.severity).color}>
                        {getSeverityBadge(item.severity).label}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {/* Suggestions */}
        {suggestions && suggestions.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <Text size={200} weight="semibold" style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>
              Suggestions
            </Text>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {suggestions.map((s, i) => (
                <li key={i} style={{ fontSize: 13, marginBottom: 2 }}>{s}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Warnings */}
        {warnings && warnings.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {warnings.map((w, i) => (
              <MessageBar key={i} intent="warning" style={{ borderRadius: 4 }}>
                <MessageBarBody>{w}</MessageBarBody>
              </MessageBar>
            ))}
          </div>
        )}

        {/* Errors */}
        {errors && errors.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {errors.map((e, i) => (
              <MessageBar key={i} intent="error" style={{ borderRadius: 4 }}>
                <MessageBarBody>{e}</MessageBarBody>
              </MessageBar>
            ))}
          </div>
        )}
      </ResultCard>

      {/* ── Breakdown card ── */}
      {breakdown && breakdown.length > 0 && (
        <ResultCard
          title="Decomposition"
          icon={<TableRegular />}
          variant="explanation"
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={thStyle}>Partie</th>
                <th style={thStyle}>Description</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((row, i) => (
                <tr key={i}>
                  <td style={tdCodeStyle}>{row.part}</td>
                  <td style={tdStyle}>{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ResultCard>
      )}
    </>
  );
};

// ─── Table styles ────────────────────────────────────────

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '4px 8px',
  borderBottom: '1px solid var(--border-color)',
  fontWeight: 600,
  fontSize: 12,
  color: 'var(--text-secondary)',
};

const tdStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderBottom: '1px solid var(--border-color)',
  verticalAlign: 'top',
};

const tdCodeStyle: React.CSSProperties = {
  ...tdStyle,
  fontFamily: 'monospace',
  whiteSpace: 'nowrap',
  color: 'var(--accent)',
};
